import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Spin,
  Divider,
  Radio,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  UserOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import {
  _get_customer_addresses,
  _create_customer_address,
  _update_customer_address,
  _delete_customer_address,
  _set_default_customer_address,
} from "../common/axios_services";
import useUserProfile from "../hooks/useUserProfile";
import StateLgaSelects from "./common/StateLgaSelects";
import AutoCompleteAddress from "./AutoCompleteAddress";

const { Option } = Select;

const AddressManager = ({
  visible,
  onClose,
  onAddressSelect,
  onAddressUpdate,
  selectedAddressId,
  title = "Manage Addresses",
}) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressSelectedViaAutocomplete, setAddressSelectedViaAutocomplete] =
    useState(false);
  const [form] = Form.useForm();
  const { userProfile } = useUserProfile();

  useEffect(() => {
    if (visible) {
      fetchAddresses();
    }
  }, [visible]);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const response = await _get_customer_addresses();
      if (response.data && response.data.data) {
        setAddresses(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
      message.error("Failed to load addresses");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAddress = async (values) => {
    try {
      setLoading(true);
      const response = await _create_customer_address(values);
      if (response.data) {
        message.success("Address created successfully");
        setModalVisible(false);
        form.resetFields();
        fetchAddresses();
        if (onAddressUpdate) onAddressUpdate();
      }
    } catch (error) {
      console.error("Error creating address:", error);
      message.error("Failed to create address");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAddress = async (values) => {
    try {
      setLoading(true);
      const response = await _update_customer_address(
        editingAddress.id,
        values,
      );
      if (response.data) {
        message.success("Address updated successfully");
        setModalVisible(false);
        setEditingAddress(null);
        form.resetFields();
        fetchAddresses();
        if (onAddressUpdate) onAddressUpdate();
      }
    } catch (error) {
      console.error("Error updating address:", error);
      message.error("Failed to update address");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      setLoading(true);
      await _delete_customer_address(addressId);
      message.success("Address deleted successfully");
      fetchAddresses();
      if (onAddressUpdate) onAddressUpdate();
    } catch (error) {
      console.error("Error deleting address:", error);
      message.error("Failed to delete address");
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      setLoading(true);
      await _set_default_customer_address(addressId);
      message.success("Default address updated");
      fetchAddresses();
      if (onAddressUpdate) onAddressUpdate();
    } catch (error) {
      console.error("Error setting default address:", error);
      message.error("Failed to set default address");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingAddress(null);
    setAddressSelectedViaAutocomplete(false); // Reset autocomplete state
    form.resetFields();
    setModalVisible(true);
  };

  const openEditModal = (address) => {
    setEditingAddress(address);
    setAddressSelectedViaAutocomplete(false);
    form.setFieldsValue(address);
    setModalVisible(true);
  };

  const handleModalOk = () => {
    form
      .validateFields()
      .then((values) => {
        if (editingAddress) {
          handleUpdateAddress(values);
        } else {
          handleCreateAddress(values);
        }
      })
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo);
        const firstError = errorInfo.errorFields?.[0]?.errors?.[0];
        if (firstError) {
          message.error(firstError);
        } else {
          message.error("Please fill in all required fields correctly");
        }
      });
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingAddress(null);
    form.resetFields();
  };

  const handleAddressSelection = (address) => {
    if (onAddressSelect) {
      onAddressSelect(address);
    }
    onClose();
  };

  if (!visible) return null;

  return (
    <>
      <Modal
        title={title}
        open={visible}
        onCancel={onClose}
        footer={null}
        width={800}
        style={{ top: 20 }}
      >
        <Spin spinning={loading}>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
              style={{ marginBottom: 16 }}
            >
              Add New Address
            </Button>
          </div>

          <div style={{ maxHeight: 500, overflowY: "auto" }}>
            {addresses.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <EnvironmentOutlined
                  style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }}
                />
                <p style={{ color: "#666", marginBottom: 16 }}>
                  No addresses found
                </p>
                <Button type="primary" onClick={openCreateModal}>
                  Add Your First Address
                </Button>
              </div>
            ) : (
              <Space direction="vertical" style={{ width: "100%" }}>
                {addresses.map((address) => (
                  <Card
                    key={address.id}
                    style={{
                      border:
                        selectedAddressId === address.id
                          ? "2px solid #1890ff"
                          : "1px solid #d9d9d9",
                      backgroundColor: address.isDefault ? "#f6ffed" : "#fff",
                    }}
                    bodyStyle={{ padding: 16 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: 8,
                          }}
                        >
                          <HomeOutlined
                            style={{ marginRight: 8, color: "#1890ff" }}
                          />
                          <span style={{ fontWeight: "bold", fontSize: 16 }}>
                            {address.label || "Unnamed Address"}
                            {address.isDefault && (
                              <span
                                style={{
                                  backgroundColor: "#52c41a",
                                  color: "white",
                                  padding: "2px 8px",
                                  borderRadius: 4,
                                  fontSize: 12,
                                  marginLeft: 8,
                                }}
                              >
                                Default
                              </span>
                            )}
                          </span>
                        </div>

                        <div style={{ marginBottom: 8, color: "#666" }}>
                          <EnvironmentOutlined style={{ marginRight: 8 }} />
                          {[
                            address.address,
                            address.city,
                            address.state,
                            address.country,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </div>

                        {address.phone && (
                          <div style={{ marginBottom: 8, color: "#666" }}>
                            <PhoneOutlined style={{ marginRight: 8 }} />
                            {address.phone}
                          </div>
                        )}

                        {address.landmark && (
                          <div style={{ color: "#666" }}>
                            <span style={{ fontWeight: "bold" }}>
                              Landmark:
                            </span>{" "}
                            {address.landmark}
                          </div>
                        )}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        <Space>
                          <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openEditModal(address)}
                          >
                            Edit
                          </Button>
                          <Popconfirm
                            title="Delete this address?"
                            description="This action cannot be undone."
                            onConfirm={() => handleDeleteAddress(address.id)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              disabled={address.isDefault}
                            >
                              Delete
                            </Button>
                          </Popconfirm>
                        </Space>

                        {!address.isDefault && (
                          <Button
                            size="small"
                            onClick={() => handleSetDefault(address.id)}
                            style={{ width: "100%" }}
                          >
                            Set as Default
                          </Button>
                        )}

                        <Button
                          type={
                            selectedAddressId === address.id
                              ? "primary"
                              : "default"
                          }
                          onClick={() => handleAddressSelection(address)}
                          style={{ width: "100%" }}
                        >
                          {selectedAddressId === address.id
                            ? "Selected"
                            : "Select This Address"}
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </Space>
            )}
          </div>
        </Spin>
      </Modal>

      <Modal
        title={editingAddress ? "Edit Address" : "Add New Address"}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            country: "Nigeria",
            phone: userProfile?.phoneNumber || "",
          }}
        >
          <Form.Item
            name="label"
            label="Address Name"
            rules={[{ required: true, message: "Please enter address name" }]}
          >
            <Input placeholder="e.g., Home, Office, etc." />
          </Form.Item>

          <Form.Item
            name="address"
            label="Street Address"
            rules={[{ required: true, message: "Please enter street address" }]}
          >
            <AutoCompleteAddress
              placeholder="Search for a landmark or nearby location (e.g., GTBank, Shoprite)"
              onChange={(val) => {
                if (!val) setAddressSelectedViaAutocomplete(false);
              }}
              onAddressSelect={(addressData) => {
                setAddressSelectedViaAutocomplete(true);
                form.setFieldsValue({
                  address:
                    addressData.address ||
                    addressData.street ||
                    form.getFieldValue("address"),
                  latitude: addressData.latitude,
                  longitude: addressData.longitude,
                  state: addressData.state,
                  city: addressData.city || addressData.locality,
                });
              }}
            />
          </Form.Item>

          <StateLgaSelects
            form={form}
            stateFieldName="state"
            lgaFieldName="city"
            initialPickupState={editingAddress?.state}
            initialPickupLga={editingAddress?.city}
            disabled={addressSelectedViaAutocomplete}
            hide={addressSelectedViaAutocomplete}
          />

          <Form.Item name="latitude" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="longitude" hidden>
            <Input />
          </Form.Item>

          {/* Location status indicator */}
          {addressSelectedViaAutocomplete ? (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                backgroundColor: "#e6f7ff",
                border: "1px solid #91d5ff",
                borderRadius: 4,
              }}
            >
              <EnvironmentOutlined
                style={{ marginRight: 8, color: "#1890ff" }}
              />
              <span style={{ color: "#1890ff", fontWeight: "bold" }}>
                Address and coordinates verified via Google Maps
              </span>
            </div>
          ) : (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                backgroundColor: "#fff7e6",
                border: "1px solid #ffd591",
                borderRadius: 4,
              }}
            >
              <span style={{ color: "#faad14", fontWeight: "bold" }}>
                Tip: Search for a landmark (e.g. GTBank) for better accuracy
              </span>
            </div>
          )}

          {!userProfile?.phoneNumber && (
            <Form.Item
              name="phone"
              label="Phone Number"
              rules={[{ required: true, message: "Please enter phone number" }]}
            >
              <Input
                type="number"
                inputMode="numeric"
                placeholder="e.g., +2348012345678"
              />
            </Form.Item>
          )}

          {userProfile?.phoneNumber && (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                backgroundColor: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: 4,
              }}
            >
              <PhoneOutlined style={{ marginRight: 8, color: "#52c41a" }} />
              <span style={{ color: "#52c41a", fontWeight: "bold" }}>
                Using profile phone: {userProfile?.phoneCode}
                {userProfile?.phoneNumber}
              </span>
            </div>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default AddressManager;
